import { Fragment, useState } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Moon, Sun, User } from "lucide-react";
import PagesEndpoints from "../../utilities/constants/PagesEndpoints";
import KeycloakService from "../config/KeycloakService";
import { useUserRefresh } from "../user/UserProvider";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";

const navigation = [
  { title: "Home", url: PagesEndpoints.HOME_PAGE_URL },
  { title: "About", url: PagesEndpoints.ABOUT_PAGE_URL },
  { title: "Services", url: PagesEndpoints.SERVICES_PAGE_URL },
  { title: "Events", url: PagesEndpoints.EVENT_PAGE_URL },
  { title: "Gallery", url: PagesEndpoints.GALLERY_PAGE_URL },
  { title: "Contact Us", url: PagesEndpoints.CONTACT_PAGE_URL },
  { title: "Book Now", url: PagesEndpoints.BOOKING_PAGE_URL },
];

const userMenu = [
  { title: "My Account", url: PagesEndpoints.ACCOUNT_PAGE_URL },
];

const NavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const setFetchUserFlag = useUserRefresh();

  // Helper to determine active state style
  const getLinkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-200 ${
      isActive
        ? "text-indigo-600 dark:text-indigo-400"
        : "text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400"
    }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200 dark:border-white/5 transition-colors duration-300">
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8"
        aria-label="Global"
      >
        {/* Logo Section */}
        <div className="flex lg:flex-1">
          <Link
            to={PagesEndpoints.HOME_PAGE_URL}
            className="-m-1.5 p-1.5 flex items-center gap-2 group"
          >
            <img
              src="/assets/images/le_prestige_hall_logo.png"
              alt="Le Prestige Hall Logo"
              className="h-10 w-auto rounded-full shadow-lg group-hover:scale-110 transition-transform duration-300"
            />
            <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-400">
              Le Prestige Hall
            </span>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-slate-700 dark:text-slate-200"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:gap-x-8">
          {navigation
            .filter((item) => item.title !== "Home")
            .map((item) => (
              <NavLink key={item.title} to={item.url} className={getLinkClass}>
                {item.title}
              </NavLink>
            ))}
        </div>

        {/* Desktop Right Section (Theme & Auth) */}
        <div className="hidden lg:flex lg:flex-1 lg:justify-end gap-4 items-center">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 dark:text-slate-400 transition-colors"
            aria-label="Toggle Theme"
          >
            {theme === "dark" ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>

          {/* Auth Logic */}
          {KeycloakService.isLoggedIn() ? (
            <Menu as="div" className="relative ml-3">
              <Menu.Button className="flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 p-1 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 transition-colors border border-slate-200 dark:border-slate-700 hover:border-indigo-500 dark:hover:border-indigo-500">
                <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                  <User className="w-5 h-5" />
                </div>
                <span className="text-slate-700 dark:text-slate-200 font-medium">
                  Account
                </span>
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-xl bg-white dark:bg-slate-900 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-slate-200 dark:border-slate-800">
                  {userMenu.map((item) => (
                    <Menu.Item key={item.title}>
                      {({ active }) => (
                        <Link
                          to={item.url}
                          className={`block px-4 py-2 text-sm ${
                            active
                              ? "bg-slate-50 dark:bg-slate-800 text-indigo-600 dark:text-indigo-400"
                              : "text-slate-700 dark:text-slate-300"
                          }`}
                        >
                          {item.title}
                        </Link>
                      )}
                    </Menu.Item>
                  ))}
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => KeycloakService.doLogout(location)}
                        className={`block w-full text-left px-4 py-2 text-sm ${
                          active
                            ? "bg-slate-50 dark:bg-slate-800 text-red-600"
                            : "text-red-500"
                        }`}
                      >
                        Sign out
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          ) : (
            <button
              onClick={() => KeycloakService.doLogin(setFetchUserFlag)}
              className="text-sm font-semibold leading-6 text-white bg-indigo-600 hover:bg-indigo-500 px-4 py-2 rounded-full shadow-lg shadow-indigo-500/30 transition-all hover:shadow-indigo-500/50"
            >
              Sign In
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Menu Dialog */}
      <Dialog
        as="div"
        className="lg:hidden"
        open={mobileMenuOpen}
        onClose={setMobileMenuOpen}
      >
        <div className="fixed inset-0 z-50 shadow-2xl" />
        <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white dark:bg-slate-950 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-slate-900/10 transition-colors duration-300">
          <div className="flex items-center justify-between">
            <Link
              to={PagesEndpoints.HOME_PAGE_URL}
              className="-m-1.5 p-1.5 flex items-center gap-2"
            >
              <img
                src="/assets/images/le_prestige_hall_logo.png"
                alt="Le Prestige Hall Logo"
                className="h-10 w-auto rounded-full shadow-lg"
              />
              <span className="text-xl font-bold text-slate-900 dark:text-white">
                Le Prestige Hall
              </span>
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-slate-700 dark:text-slate-200"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-slate-500/10">
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <NavLink
                    key={item.title}
                    to={item.url}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 ${
                        isActive
                          ? "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400"
                          : "text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`
                    }
                  >
                    {item.title}
                  </NavLink>
                ))}
              </div>
              <div className="py-6 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    Appearance
                  </span>
                  <button
                    onClick={toggleTheme}
                    className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
                  >
                    {theme === "dark" ? (
                      <Sun className="w-5 h-5" />
                    ) : (
                      <Moon className="w-5 h-5" />
                    )}
                  </button>
                </div>

                <div className="h-px bg-slate-200 dark:bg-slate-800 my-2" />

                {KeycloakService.isLoggedIn() ? (
                  <>
                    <Link
                      to={PagesEndpoints.ACCOUNT_PAGE_URL}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-2 py-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="h-10 w-10 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                        <User className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-white">
                          My Account
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Manage your profile
                        </p>
                      </div>
                    </Link>
                    <button
                      onClick={() => {
                        KeycloakService.doLogout(location);
                        setMobileMenuOpen(false);
                      }}
                      className="block w-full rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Log out
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      KeycloakService.doLogin(setFetchUserFlag);
                      setMobileMenuOpen(false);
                    }}
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    Log in
                  </button>
                )}
              </div>
            </div>
          </div>
        </Dialog.Panel>
      </Dialog>
    </header>
  );
};

export default NavBar;
